import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const DEFAULT_APP_NAME = "anpaGestiona";

function resolveAppName(raw: string | null | undefined): string {
  const trimmed = (raw ?? "").trim();
  return trimmed !== "" ? trimmed : DEFAULT_APP_NAME;
}

function shortNameFrom(appName: string): string {
  if (appName.length <= 12) return appName;
  return appName.slice(0, 12).trimEnd();
}

function apiBase(): string {
  return (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");
}

function applyDocumentBrand(appName: string, shortName: string) {
  document.title = appName;

  let appleTitle = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]'
  );
  if (!appleTitle) {
    appleTitle = document.createElement("meta");
    appleTitle.name = "apple-mobile-web-app-title";
    document.head.appendChild(appleTitle);
  }
  appleTitle.content = shortName;

  // Manifest dinámico desde API (nome da ANPA). Cache-bust ao cambiar o nome.
  const manifestUrl = `${apiBase()}/branding/manifest?v=${encodeURIComponent(appName)}`;
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  link.href = manifestUrl;
}

export const useBrandStore = defineStore("brand", () => {
  const anpaName = ref<string | null>(null);
  const loaded = ref(false);

  const appName = computed(() => resolveAppName(anpaName.value));
  const shortName = computed(() => shortNameFrom(appName.value));

  function apply() {
    applyDocumentBrand(appName.value, shortName.value);
  }

  function setAnpaName(name: string | null | undefined) {
    const trimmed = (name ?? "").trim();
    anpaName.value = trimmed !== "" ? trimmed : null;
    apply();
  }

  async function fetchBranding() {
    try {
      const res = await fetch(`${apiBase()}/branding`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        setAnpaName(null);
        return;
      }
      const json = await res.json();
      if (json?.data?.name != null) {
        setAnpaName(json.data.name);
      } else if (typeof json?.data?.app_name === "string") {
        setAnpaName(
          json.data.app_name === DEFAULT_APP_NAME ? null : json.data.app_name
        );
      } else {
        setAnpaName(null);
      }
    } catch {
      setAnpaName(null);
    } finally {
      loaded.value = true;
    }
  }

  return {
    anpaName,
    loaded,
    appName,
    shortName,
    apply,
    setAnpaName,
    fetchBranding,
  };
});
